<template>
  <v-card v-if="type.is_active" variant="elevated" elevation="6">
    <v-card-item>
      <v-card-title class="text-body-1 font-weight-bold text-wrap">
        {{ type.producttype_name }}
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-row no-gutters>
        <v-col cols="12" xxl="8" xl="8" lg="8" md="8">
          <div class="d-flex align-center">
            <v-icon class="me-1 align-self-start pt-1" size="medium">mdi-calendar-multiselect</v-icon>
            <div>
              {{ convertTwoDates(product.event_start, product.event_end, product.utc) }}
            </div>
          </div>

          <div class="d-flex align-center">
            <v-icon class="me-1 align-self-start pt-1" size="medium">mdi-clock-time-three-outline</v-icon>
            <div>
              {{ $t("event-card.time", { time: convertHour(product.event_start, product.event_end, product.utc) }) }}
            </div>
          </div>

          <div class="d-flex align-center">
            <v-icon class="me-1 align-self-start pt-1" size="medium">mdi-note-edit-outline</v-icon>
            <div>
              {{
                $t("event-card.pullout-date", {
                  date: formatDate(
                    product.product_pull_out_date,
                    "DD MMMM YYYY, HH.mm",
                    product.utc
                  ),
                })
              }}
            </div>
          </div>

          <div v-if="type.validation_dates?.length > 0" class="d-flex align-center">
            <v-icon class="me-1 align-self-start pt-1" size="medium">mdi-clock-time-five</v-icon>
            <div>
              {{
                $t("event-card.ticket-validation")
              }}
            </div>

            <v-tooltip content-class="bg-widget-color" :open-on-hover="!isMobile()" :open-on-click="isMobile()"
              scroll-strategy="close">
              <template v-slot:activator="{ props }">
                <v-chip v-bind="props" label>
                  {{ $t("event-card.validation-dates", { day: type.validation_dates?.length }) }}
                </v-chip>
              </template>
              <div v-for="(date, index) in type.validation_dates" :key="index">
                {{
                  convertDate(date, "DD MMMM YYYY")
                }}
              </div>
            </v-tooltip>
          </div>
        </v-col>

        <v-col class="d-flex align-end justify-sm-start justify-md-end" cols="12" xxl="4" xl="4" lg="4" md="4" sm="5">
          <!-- handling unlimited qty -->
          <v-chip v-if="type.is_limited &&
            type.producttype_qty -
            type.producttype_booked_qty <=
            type.qty_threshold_sale" color="error" class="text-caption" label>
            {{ $t("event-card.out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="type.is_limited && type.producttype_qty -
            type.producttype_booked_qty <=
            type.qty_threshold_ui" color="warning" class="text-caption" label>
            {{ $t("event-card.almost-out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="type.is_limited && type.qty_visibility" color="widget-color" class="text-caption" label>
            {{ $t("event-card.remaining-tickets") }}:&nbsp;
            <span>{{
              type.producttype_qty - type.producttype_booked_qty >= 0
                ? type.producttype_qty - type.producttype_booked_qty
                : 0
            }}</span>
          </v-chip>
        </v-col>
      </v-row>

      <v-divider class="my-2 border-dashed" color="black" thickness="3"></v-divider>
    </v-card-text>

    <v-card-text class="pt-0">
      <!-- handling stok habis, stok kurang dari max order, tambah stok tidak boleh lebih dari max order -->
      <v-row no-gutters align="center">
        <v-col cols="12" xxl="8" xl="8" lg="8" md="8" sm="8">
          <div v-if="type.discounted_price !== null && type.discounted_price >= 0"
            class="text-h6 text-decoration-line-through font-weight-bold text-left">
            {{ numberToRupiah(type.producttype_price) }}
          </div>
          <div v-if="type.producttype_id == 1682 && type.producttype_minimum_order == type.producttype_maximum_order"
            class="text-h6 font-weight-bold text-left">
            {{ numberToRupiah(type.discounted_price !== null && type.discounted_price >= 0 ? type.discounted_price *
              type.producttype_minimum_order :
              type.producttype_price * type.producttype_minimum_order) }}
          </div>
          <div v-else class="text-h6 font-weight-bold text-left">
            {{ numberToRupiah(type.discounted_price !== null && type.discounted_price >= 0 ? type.discounted_price :
              type.producttype_price) }}
          </div>
        </v-col>
        <v-col class="d-flex justify-end" cols="12" xxl="4" xl="4" lg="4" md="4" sm="4">
          <v-chip v-if="
            validation.quantity.isOutOfStock(type) ||
            validation.quantity.isLessThanMinOrder(type) || (type.is_limited &&
              type.producttype_qty -
              type.producttype_booked_qty <=
              type.qty_threshold_sale)
          " color="error">
            {{ $t("event-card.out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="product.is_expired" color="error">
            {{ $t("event-card.pullout-end") }}
          </v-chip>
          <v-btn v-else-if="!findProductInCart(type.producttype_id)" variant="flat" :disabled="product.is_upcoming"
            @click="addToCart(type, type.producttype_minimum_order)" color="widget-color"
            class="text-none text-caption">
            {{ $t("event-card.choose-ticket") }}
          </v-btn>

          <div class="d-flex align-center" v-else>
            <v-icon size="large" color="widget-color" class="mr-2" @click="removeFromCart(type)">mdi-trash-can</v-icon>
            <v-btn class="mx-2" color="widget-color" size="small" rounded="lg" @click="reduceFromCart(type)"
              icon="mdi-minus" :disabled="!validation.cart.canReduceFromCart(
                type,
                findProductInCart(type.producttype_id)?.qty
              )
                "></v-btn>
            <span>
              {{ findProductInCart(type.producttype_id)?.qty }}
            </span>
            <v-btn class="mx-2" color="widget-color" size="small" rounded="lg" @click="addToCart(type)" icon="mdi-plus"
              :disabled="!validation.cart.canAddToCart(
                type,
                findProductInCart(type.producttype_id)?.qty)"></v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-snackbar v-model="snackbar.show" rounded="pill" location="top" color="white" width="auto" min-width="auto"
      :timeout="2000">
      <div :class="snackbar.error ? 'text-error' : 'text-primary'" class="font-weight-bold">
        <v-icon :icon="snackbar.error ? 'mdi-close-circle' : 'mdi-check-circle'"
          :color="snackbar.error ? 'error' : 'primary'"></v-icon>
        {{ snackbar.text }}
      </div>
    </v-snackbar>
  </v-card>

</template>

<script>
import { category } from "@/config/productCategory";
import { utcToLocale } from "@/utils/dateFormatter";
import { numberToRupiah } from "@/utils/numberFormatter";
import * as validation from "@/utils/validation";
import moment from "moment";


export default {
  props: {
    type: {
      type: Object,
      require: true,
    },
  },

  computed: {
    product() {
      return this.$store.state.product;
    },
    location() {
      return this.product.product_location
    }
  },

  data() {
    return {
      validation: {
        quantity: validation.default.quantity,
        cart: validation.default.cart,
      },
      CATEGORY: category,
      snackbar: {
        show: false,
        text: null,
      },
    };
  },

  methods: {
    numberToRupiah(number) {
      return numberToRupiah(number);
    },
    findProductInCart(productTypeID) {
      return this.$store.getters.findProductInCart(productTypeID);
    },
    removeFromCart(productType) {
      this.$store.commit("removeProductFromCart", {
        productType: productType,
      });
    },
    reduceFromCart(productType) {
      this.$store.commit("reduceProductFromCart", {
        productType: productType,
      });
    },
    addToCart(productType, qty = 1) {
      this.$store.commit("addProductToCart", {
        productType: productType,
        qty: qty,
      });
    },

    showSnackbar(text, error = false) {
      this.snackbar.text = text;
      this.snackbar.show = true;
      this.snackbar.error = error;
    },

    formatDate(isoDate, format, utc) {
      const offsetMinutes = utc * 60;
      let timezone = "WIB/GMT+7";
      if (utc === 8) timezone = "WITA/GMT+8";
      else if (utc === 9) timezone = "WIT/GMT+9";
      else if (utc !== 7 && utc !== 8 && utc !== 9) {
        timezone = `GMT${utc >= 0 ? `+${utc}` : utc}`;
      }
      return moment
        .utc(isoDate)
        .utcOffset(offsetMinutes)
        .format(`${format} [${timezone}]`);
    },

    convertDate(isoDate) {
      return utcToLocale(isoDate, "DD MMM YYYY");
    },

    convertTwoDates(start, end, utc) {
      const offsetMinutes = utc * 60;

      const startDate = moment.utc(start).utcOffset(offsetMinutes);
      const endDate = moment.utc(end).utcOffset(offsetMinutes);

      const sameDay = startDate.isSame(endDate, 'day');
      const sameMonth = startDate.isSame(endDate, 'month');
      const sameYear = startDate.isSame(endDate, 'year');

      if (sameDay && sameMonth && sameYear) {
        // 1. Hari, bulan, dan tahun sama
        return startDate.format('ddd, DD MMM YYYY');
      } else if (sameMonth && sameYear) {
        // 2. Bulan & tahun sama, hari beda
        return `${startDate.format('ddd')}-${endDate.format('ddd')}, ${startDate.format('DD')}-${endDate.format('DD MMM YYYY')}`;
      } else if (sameYear) {
        // 3. Tahun sama, bulan atau hari beda
        return `${startDate.format('ddd')}-${endDate.format('ddd')}, ${startDate.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}`;
      } else {
        // 4. Tahun beda
        return `${startDate.format('ddd')}-${endDate.format('ddd')}, ${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
      }
    },

    convertHour(start, end, utc) {
      const offsetMinutes = utc * 60;
      const startDate = moment.utc(start).utcOffset(offsetMinutes).format("HH:mm")
      const endDate = moment.utc(end).utcOffset(offsetMinutes).format("HH:mm")
      let timezone = "WIB/GMT+7";

      if (utc === 8) timezone = "WITA/GMT+8";
      else if (utc === 9) timezone = "WIT/GMT+9";
      else if (utc !== 7 && utc !== 8 && utc !== 9) {
        timezone = `GMT${utc >= 0 ? `+${utc}` : utc}`;
      }
      return `${startDate} - ${endDate} ${timezone}`;
    },

    isMobile() {
      return this.$vuetify.display.mobile;
    },
  },
};
</script>

<style></style>